import {coerceBooleanProperty} from '@angular/cdk/coercion';
import {Injectable} from '@angular/core';
import {OnDestroyMixin, untilComponentDestroyed} from '@w11k/ngx-componentdestroyed';
import {BehaviorSubject, Observable, of, throwError} from 'rxjs';
import {catchError, distinctUntilChanged, filter, map, shareReplay, switchMap, take} from 'rxjs/operators';
import {GpHoldingListItemReqRes} from 'src/app/dashboard/shared/holding/GpHoldingListItem.model';
import {RoutingService} from 'src/app/services/routing.service';

import {AccountType} from 'src/app/shared/enums/AccountType.enum';
import {UnitApplicationParentType} from 'src/app/shared/enums/unit-bank-account/UnitApplicationParentType.enum';
import {UnitBankAccountReqRes} from 'src/app/shared/models/gp/UnitBankAccountReqRes.model';
import {UnitBankAccountFee} from 'src/app/shared/models/unit-bank-account/UnitBankAccountFee.model';
import {UnitBusinessApplicationReqRes} from 'src/app/shared/models/unit-bank-account/UnitBusinessApplicationReqRes.model';
import {TerraUtils} from 'src/app/shared/TerraUtils';
import {UnitBankAccountDataService} from 'src/app/services/shared/unit-bank-account-data.service';
import UnitAccountProvider from '../../../enums/UnitAccountProvider.enum';

export enum CreateUnitBankAccountStep {
  FillDetails,
  Review
}

export interface UnitCreateBankAccountModel {
  holding?: GpHoldingListItemReqRes;
  accountNickname: string;
  accountType: AccountType;
  preselectedType: UnitApplicationParentType;
  unitApplicationId: number;
}

@Injectable()
export class UnitCreateBankAccountService extends OnDestroyMixin {


  /** To make sure the create operation will only happen once. */
    // private idempotencyKey: string;

  private _step$ = new BehaviorSubject(CreateUnitBankAccountStep.FillDetails);
  step$ = this._step$.pipe(shareReplay(1));

  private _bankAccountFormDetails$ = new BehaviorSubject<UnitCreateBankAccountModel>({
    accountNickname: null,
    accountType: AccountType.Primary,
    holding: null,
    preselectedType: UnitApplicationParentType.Holding,
    unitApplicationId: null
  });

  private _holdingId$ = new BehaviorSubject<number>(null);
  holdingId$ = this._holdingId$.asObservable().pipe(shareReplay(1));

  bankAccountFormDetails$ = this._bankAccountFormDetails$.pipe(shareReplay(1));

  fees$ = this.bankAccountFormDetails$.pipe(
    filter(account => coerceBooleanProperty(account)),
    map(account => account.accountType),
    distinctUntilChanged(),
    switchMap(accountType => this.getAccountFees(accountType)),
    shareReplay(1)
  );

  isCreateFailed$ = new BehaviorSubject(false);

  constructor(
    private routingService: RoutingService,
    private unitBankAccountDataService: UnitBankAccountDataService) {
    super();
    this.step$.pipe(untilComponentDestroyed(this)).subscribe(
      _ => TerraUtils.scrollToTop()
    );

    // this.idempotencyKey = TerraUtils.uuidv4();
  }

  originUrl(parentType: UnitApplicationParentType) {
    switch (parentType) {
      case UnitApplicationParentType.Holding:
        return of(this.routingService.bankAccountsPage('cre')).pipe(shareReplay(1));
      case UnitApplicationParentType.Organization:
        return of(this.routingService.bankAccountsPage('cre_accounts')).pipe(shareReplay(1));
      case UnitApplicationParentType.LP:
        return of(this.routingService.lpBankAccounts).pipe(shareReplay(1));
      default:
        return of(this.routingService.bankAccountsPage('cre')).pipe(shareReplay(1));
    }
  }

  updateBankAccountFormDetails(model: UnitCreateBankAccountModel) {
    this._bankAccountFormDetails$.next({...model});
  }

  continueToReview(model: UnitCreateBankAccountModel) {
    this.updateBankAccountFormDetails(model);
    this._step$.next(CreateUnitBankAccountStep.Review);
  }

  createBankAccount() {
    return this.generateSubmitModel().pipe(
      switchMap(model => this.unitBankAccountDataService.create(model)),
      catchError(error => {
        this.isCreateFailed$.next(true);
        return throwError(error);
      })
    );
  }

  passHoldingId(holdingId) {
    if (holdingId) {
      this._holdingId$.next(holdingId);
    }
  }

  backToFirstStep() {
    this._step$.next(CreateUnitBankAccountStep.FillDetails);
  }

  private generateSubmitModel(): Observable<UnitBankAccountReqRes> {
    return this.bankAccountFormDetails$.pipe(
      take(1),
      map(formValues => {
        const model = new UnitBankAccountReqRes();
        // model.idempotencyKey = this.idempotencyKey;
        model.unitApplication = new UnitBusinessApplicationReqRes();
        model.unitApplicationId = formValues.holding?.unitApplicationId || formValues.unitApplicationId;
        model.unitApplication.holdingId = formValues.holding?.id;
        model.accountNickname = formValues.accountNickname;
        model.accountType = formValues.accountType;
        model.provider = UnitAccountProvider.Thread;
        return model;
      }));
  }

  private getAccountFees(accountType: AccountType): Observable<UnitBankAccountFee[]> {
    // TODO: Once we know what the Fees mean, replace with real fees.
    return of(accountType ? this.getFakeFees() : null);
  }

  /** TODO: Remove this method once we know what the fees mean, and get the real fees. */
  private getFakeFees(): UnitBankAccountFee[] {
    return [
      {
        type: 'Domestic Distributions Fee',
        description: 'Proin maximus sem ac libero convallis egestas.',
        fee: '0.01% of Amount'
      },
      {
        type: 'International Distributions Fee',
        description: 'Lorem ipsum dolor sit amet, consectetur adipiscing elit.',
        fee: '$5 monthly'
      },
      {
        type: 'Other Distributions Fee',
        description: 'Suspendisse molestie, ligula non consectetur viverra.',
        fee: '1% of Amount'
      }
    ];
  }
}

